/**
 * Shared setting-change validation primitives.
 *
 * This module lives in `helpers/` (not a feature directory) because the
 * validation model is not specific to bulk editing — any settings editor
 * (equipment details, scheduled apps, etc.) can reuse it.
 *
 * ## How it works
 *
 * 1. The **BFF** enriches every setting ref with validation constraints
 *    (e.g. `minLength: 1` for number/string/enum settings). These arrive
 *    on the client as `ref.validations` inside the table row data.
 *
 * 2. The **caller** (e.g. the save dialog) maps pending changes into
 *    `PendingSettingChangeValidationInput` objects, passing along the
 *    platform-provided validations payload from the ref.
 *
 * 3. The caller invokes `validateSettingChanges()` with a rule resolver
 *    (`getRulesForChange`) that inspects each change's `platformValidations`
 *    and returns the appropriate rules. This keeps rule selection data-driven
 *    — the client never hardcodes which settings need which checks.
 *
 * 4. Rules return `SettingValidationIssue[]`. Issues with `blocksSave: true`
 *    disable the save button; their `message` is shown in a tooltip.
 *
 * ## Adding new validations
 *
 * - Create a new `SettingValidationRule` that reads the relevant field from
 *   `change.platformValidations` (e.g. `max_value`, `pattern`).
 * - Register it in `getPlatformValidationRulesForChange` (or a similar
 *   per-change resolver) so it fires only when the BFF sends the constraint.
 * - The BFF must also send the constraint — see `EnsureSettingValidations`
 *   in `bff/clients/ecp/internal/shared/validations.go`.
 */

// TODO: This message is hardcoded because the BFF Validations struct has no
// per-constraint message field. When the validation API is redesigned to
// support an array of constraint objects with individual messages, read the
// message from platformValidations instead of hardcoding it here.
export const EMPTY_VALUE_VALIDATION_MESSAGE = 'Cannot set to empty';

// ─── Types ───────────────────────────────────────────────────────────────────

/** A single pending change ready for validation. */
export interface PendingSettingChangeValidationInput {
  /** Unique key for this change (typically `${rowId}-${columnId}`). */
  changeId: string;
  /** Raw form value the user has entered (always a string). */
  currentValue: string;
  /**
   * Validation constraints from the BFF, passed through from `ref.validations`.
   *
   * Typed as `unknown` because the BFF schema may evolve. Platform rules
   * narrow this to the shape they need (e.g. `{ min_length: number }`).
   */
  platformValidations?: unknown;
}

/** A single validation problem for a setting change. */
export interface SettingValidationIssue {
  /** Machine-readable identifier (e.g. `'empty-value'`, `'min'`, `'max'`). */
  code: string;
  /** Human-readable message shown in the UI (e.g. tooltip text). */
  message: string;
  /** When true, the save button is disabled until this issue is resolved. */
  blocksSave: boolean;
}

/**
 * A function that evaluates a single change and returns zero or more issues.
 *
 * Rules are pure functions with no side effects — they read
 * `change.currentValue` and `change.platformValidations`, then return issues.
 */
export type SettingValidationRule = (
  change: PendingSettingChangeValidationInput
) => SettingValidationIssue[];

/** Options for `validateSettingChanges`. */
export interface ValidateSettingChangesOptions {
  /** Rules applied to every change (e.g. a blanket "no empty values" rule). */
  rules?: SettingValidationRule[];
  /**
   * Per-change rule resolver. Called once per change; returns rules that
   * apply only to that change based on its `platformValidations` payload.
   *
   * Use this when different settings have different constraints — the
   * resolver inspects the platform data and picks the right rules.
   */
  getRulesForChange?: (change: PendingSettingChangeValidationInput) => SettingValidationRule[];
}

/** Aggregated validation result for a batch of changes. */
export interface SettingValidationResult {
  /** Map of changeId → issues. Only changes with issues appear here. */
  issuesByChangeId: Record<string, SettingValidationIssue[]>;
  /** True if any issue across all changes has `blocksSave: true`. */
  hasBlockingIssues: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hasEmptyValue = (value: string): boolean => value.trim().length === 0;
const REQUIRED_MIN_LENGTH = 1;

/**
 * Extracts `min_length` (or `minLength`) from the platform validation payload.
 * Returns `undefined` if the field is missing, non-numeric, or non-finite.
 *
 * Handles both snake_case (BFF JSON) and camelCase (SDK-transformed) shapes.
 */
const getPlatformMinLength = (platformValidations: unknown): number | undefined => {
  if (!platformValidations || typeof platformValidations !== 'object') {
    return undefined;
  }

  const payload = platformValidations as {
    min_length?: unknown;
    minLength?: unknown;
  };
  const value = payload.min_length ?? payload.minLength;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return value;
};

// ─── Rules ───────────────────────────────────────────────────────────────────

/**
 * Blocks save when `currentValue` is empty or whitespace-only.
 *
 * Not exported — only used internally by `platformRequiredSettingValidationRule`.
 * The BFF controls which settings get this check by setting `minLength >= 1`
 * on string-representable kinds (number, string, enum).
 */
const emptyValueRule = (change: PendingSettingChangeValidationInput): SettingValidationIssue[] => {
  if (!hasEmptyValue(change.currentValue)) {
    return [];
  }

  return [
    {
      code: 'empty-value',
      message: EMPTY_VALUE_VALIDATION_MESSAGE,
      blocksSave: true,
    },
  ];
};

/**
 * Checks whether the platform requires a non-empty value (`minLength >= 1`)
 * and, if so, delegates to `emptyValueRule`.
 *
 * This is the rule that callers register — it gates the empty check on
 * platform data so boolean/schedule/sequence settings (which don't receive
 * `minLength` from the BFF) are never flagged.
 */
export const platformRequiredSettingValidationRule = (
  change: PendingSettingChangeValidationInput
): SettingValidationIssue[] => {
  const minLength = getPlatformMinLength(change.platformValidations);
  if (minLength === undefined || minLength < REQUIRED_MIN_LENGTH) {
    return [];
  }
  return emptyValueRule(change);
};

/**
 * Per-change rule resolver for platform-driven validations.
 *
 * Inspects each change's `platformValidations` and returns the rules that
 * apply. Currently only checks `minLength`; extend this function when the
 * BFF sends additional constraints (e.g. `max_value`, `pattern`).
 *
 * Usage (in a save dialog):
 * ```ts
 * validateSettingChanges(inputs, {
 *   getRulesForChange: getPlatformValidationRulesForChange,
 * });
 * ```
 */
export const getPlatformValidationRulesForChange = (
  change: PendingSettingChangeValidationInput
): SettingValidationRule[] => {
  const minLength = getPlatformMinLength(change.platformValidations);
  if (minLength !== undefined && minLength >= REQUIRED_MIN_LENGTH) {
    return [platformRequiredSettingValidationRule];
  }
  return [];
};

// ─── Main entry point ────────────────────────────────────────────────────────

/**
 * Validates a batch of pending setting changes against the provided rules.
 *
 * For each change, runs all global rules plus any per-change rules returned
 * by `getRulesForChange`. Collects issues keyed by `changeId` and sets
 * `hasBlockingIssues` if any issue has `blocksSave: true`.
 *
 * @param changes - The pending changes to validate.
 * @param options - Global rules and/or a per-change rule resolver.
 * @returns Aggregated issues by changeId, plus a blocking flag.
 */
export const validateSettingChanges = (
  changes: PendingSettingChangeValidationInput[],
  options: ValidateSettingChangesOptions = {}
): SettingValidationResult => {
  const globalRules = options.rules ?? [];
  const issuesByChangeId: Record<string, SettingValidationIssue[]> = {};
  let hasBlockingIssues = false;

  for (const change of changes) {
    const perChangeRules = options.getRulesForChange?.(change) ?? [];
    const effectiveRules = [...globalRules, ...perChangeRules];
    const issues = effectiveRules.flatMap((rule) => rule(change));
    if (issues.length === 0) continue;

    issuesByChangeId[change.changeId] = issues;
    if (issues.some((issue) => issue.blocksSave)) {
      hasBlockingIssues = true;
    }
  }

  return {
    issuesByChangeId,
    hasBlockingIssues,
  };
};
